import { ArrowRight, HelpCircle } from "lucide-react";
import type { Decision, Question } from "@/lib/arch-parser";
import { renderMarkdown } from "@/lib/arch-parser/parse-markdown";
import { cn } from "@/lib/utils";

function StatusBadge({ status }: { status: Question["status"] }) {
  const cls =
    status === "answered"
      ? "bg-success-soft text-success-ink"
      : status === "deferred"
        ? "bg-surface-tint text-ink-muted"
        : "bg-warning-soft text-warning-ink";
  return (
    <span className={cn("text-[10px] uppercase tracking-wide rounded px-2 py-0.5", cls)}>
      {status}
    </span>
  );
}

export function QuestionPanel({
  question,
  resolvedDecision,
  onSelectDecision,
}: {
  question: Question;
  // Pre-resolved by the caller from arch.decisions; passed in rather than
  // re-resolved here so the panel stays presentation-only.
  resolvedDecision?: Decision;
  // Fires when the user clicks the answered_by link. Caller decides whether
  // to switch selection, navigate, etc.
  onSelectDecision?: (decisionId: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2">
        <HelpCircle className="h-4 w-4 mt-1 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={question.status} />
            {question.owner && (
              <span className="text-[10px] text-muted-foreground">owner: {question.owner}</span>
            )}
          </div>
        </div>
      </div>

      {question.context && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Context
          </div>
          <div
            className="prose-sm max-w-none text-sm leading-relaxed [&_p]:my-1 [&_code]:font-mono"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(question.context) }}
          />
        </div>
      )}

      {question.candidates && question.candidates.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Candidates being considered
          </div>
          <ul className="space-y-1 text-sm">
            {question.candidates.map((c) => (
              <li
                key={c}
                className="rounded-md border bg-card px-3 py-1.5 text-foreground/90 leading-snug"
              >
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}

      {question.blocking && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Why this is blocking
          </div>
          <p className="text-sm leading-relaxed text-foreground/90">{question.blocking}</p>
        </div>
      )}

      {question.status === "answered" && question.answered_by && (
        <div className="space-y-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Answered by
          </div>
          {resolvedDecision ? (
            <button
              type="button"
              onClick={() => onSelectDecision?.(resolvedDecision.id)}
              className="w-full text-left text-xs rounded-md border bg-card hover:bg-muted transition-colors px-3 py-2 flex items-center gap-2"
            >
              <span className="font-medium truncate">
                {resolvedDecision.title ?? resolvedDecision.label}
              </span>
              <ArrowRight className="h-3 w-3 ml-auto shrink-0 text-muted-foreground" />
            </button>
          ) : (
            <div className="text-xs text-muted-foreground italic">
              Linked decision <code className="font-mono">{question.answered_by}</code> not found —
              may have been removed or renamed.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
